import { useId, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Eye, EyeOff, Loader2, AlertTriangle, Info, CheckCircle2, XCircle, Inbox } from "lucide-react";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");
export { cx };

type Variant = "primary" | "secondary" | "danger" | "ghost" | "dangerGhost";
// docs/15 "Button": primary carries elevation; secondary is a raised white control; ghost variants use the overlay tint.
const variants: Record<Variant, string> = {
  primary: "bg-primary text-white border-primary shadow-e1 hover:bg-primary-dark hover:shadow-e2 active:shadow-e1",
  secondary: "bg-surface text-ink border-line-strong shadow-e1 hover:border-ink-3/50 hover:bg-paper active:bg-line/40",
  danger: "bg-surface text-danger-ink border-danger-ink/35 shadow-e1 hover:bg-danger hover:border-danger-ink/60",
  ghost: "elevate bg-transparent text-primary border-transparent",
  dangerGhost: "bg-transparent text-danger-ink border-transparent hover:bg-danger",
};

export function Button({
  variant = "primary",
  loading = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        "inline-flex min-h-10 items-center justify-center gap-2 rounded-ctl border px-4 py-2 text-sm font-semibold",
        "transition-[background-color,border-color,box-shadow,transform] duration-[var(--duration-base)] ease-[var(--ease-out)] active:translate-y-px",
        "disabled:pointer-events-none disabled:border-line disabled:bg-locked disabled:text-ink-3 disabled:shadow-none",
        variants[variant],
        className,
      )}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

interface FieldShellProps {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
  id: string;
  children: ReactNode;
  required?: boolean | undefined;
}

export function FieldShell({ label, error, hint, id, children, required }: FieldShellProps) {
  return (
    <div className="my-3">
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-semibold text-ink">
        {label}
        {required && <span className="text-muted font-normal"> (obrigatório)</span>}
      </label>
      {children}
      {hint && !error && <p id={`${id}-hint`} className="mt-1 text-xs text-muted">{hint}</p>}
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs font-medium text-danger-ink">
          {error}
        </p>
      )}
    </div>
  );
}

// docs/15 "Input": subtle inset, border darkens on hover, teal focus halo (global :focus rule).
const control =
  "block w-full rounded-ctl border border-field bg-surface px-3 py-2.5 text-[15px] text-ink placeholder:text-ink-3/80 " +
  "shadow-[inset_0_1px_1px_rgb(13_40_69/0.04)] transition-[border-color,box-shadow] duration-[var(--duration-base)] hover:border-ink-3/60 " +
  "read-only:bg-locked read-only:text-muted read-only:hover:border-field disabled:bg-locked disabled:text-muted aria-[invalid=true]:border-danger-ink";

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string | undefined; hint?: ReactNode; ref?: Ref<HTMLInputElement> };

export function TextField({ label, error, hint, id, ref, className, required, ...rest }: InputProps) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldShell label={label} error={error} hint={hint} id={fid} required={required}>
      <input
        ref={ref}
        id={fid}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fid}-error` : hint ? `${fid}-hint` : undefined}
        className={cx(control, className)}
        {...rest}
      />
    </FieldShell>
  );
}

export function PasswordField({ label, error, hint, id, ref, ...rest }: InputProps) {
  const auto = useId();
  const fid = id ?? auto;
  const [visible, setVisible] = useState(false);
  return (
    <FieldShell label={label} error={error} hint={hint} id={fid}>
      <div className="relative">
        <input
          ref={ref}
          id={fid}
          type={visible ? "text" : "password"}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fid}-error` : hint ? `${fid}-hint` : undefined}
          className={cx(control, "pr-12")}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
          aria-pressed={visible}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-ctl text-muted hover:text-ink"
        >
          {visible ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
        </button>
      </div>
    </FieldShell>
  );
}

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; error?: string | undefined; hint?: ReactNode; ref?: Ref<HTMLTextAreaElement> };

export function TextArea({ label, error, hint, id, ref, className, required, ...rest }: TextAreaProps) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldShell label={label} error={error} hint={hint} id={fid} required={required}>
      <textarea
        ref={ref}
        id={fid}
        rows={3}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fid}-error` : hint ? `${fid}-hint` : undefined}
        className={cx(control, "autogrow", className)}
        {...rest}
      />
    </FieldShell>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & { label: string; error?: string | undefined; hint?: ReactNode; ref?: Ref<HTMLSelectElement> };

export function SelectField({ label, error, hint, id, ref, className, children, required, ...rest }: SelectProps) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldShell label={label} error={error} hint={hint} id={fid} required={required}>
      <select
        ref={ref}
        id={fid}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fid}-error` : hint ? `${fid}-hint` : undefined}
        // <select> always matches :read-only; keep it looking editable.
        className={cx(control, "read-only:bg-white read-only:text-ink", className)}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  );
}

type Tone = "success" | "info" | "warn" | "danger";
const tones: Record<Tone, { cls: string; Icon: typeof Info }> = {
  success: { cls: "bg-soft text-soft-ink", Icon: CheckCircle2 },
  info: { cls: "bg-info text-info-ink", Icon: Info },
  warn: { cls: "bg-warn text-warn-ink", Icon: AlertTriangle },
  danger: { cls: "bg-danger text-danger-ink", Icon: XCircle },
};

export function Alert({ tone = "info", title, children, action, role }: { tone?: Tone; title?: string; children?: ReactNode; action?: ReactNode; role?: "alert" | "status" }) {
  const { cls, Icon } = tones[tone];
  return (
    <div role={role ?? (tone === "danger" ? "alert" : "status")} className={cx("my-4 flex gap-3 rounded-ctl px-4 py-3 text-sm ring-1 ring-inset ring-current/10", cls)}>
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? "mt-0.5" : ""}>{children}</div>}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

export function Panel({ children, className, as: As = "section" }: { children: ReactNode; className?: string; as?: "section" | "div" | "article" }) {
  return <As className={cx("rounded-panel border border-line bg-surface p-5 shadow-e1", className)}>{children}</As>;
}

type PillTone = "green" | "gold" | "blue" | "gray" | "red";
// docs/15 "Pill": tinted pair, hairline ring, leading status dot.
const pills: Record<PillTone, string> = {
  green: "bg-soft text-soft-ink",
  gold: "bg-[#fff0d7] text-[#76511b]",
  blue: "bg-info text-info-ink",
  gray: "bg-[#edf1f5] text-[#46586d]",
  red: "bg-danger text-danger-ink",
};
export function Pill({ tone = "green", children }: { tone?: PillTone; children: ReactNode }) {
  return (
    <span className={cx("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ring-1 ring-inset ring-current/15", pills[tone])}>
      <span className="size-1.5 rounded-full bg-current opacity-70" aria-hidden />
      {children}
    </span>
  );
}

export function Skeleton({ lines = 3, label = "Carregando…" }: { lines?: number; label?: string }) {
  return (
    <div role="status" aria-live="polite" className="space-y-3 py-2">
      <span className="sr-only">{label}</span>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skeleton h-4 rounded-chip" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

export function EmptyState({ title, children, action, icon: Icon = Inbox }: { title: string; children?: ReactNode; action?: ReactNode; icon?: typeof Inbox }) {
  return (
    <div className="rounded-panel border border-line bg-surface px-5 py-10 text-center shadow-e1">
      <span className="mx-auto mb-3 flex size-12 items-center justify-center rounded-[14px] bg-gradient-to-b from-soft to-[#cfeee9] text-soft-ink ring-1 ring-inset ring-soft-ink/10" aria-hidden>
        <Icon className="size-5" />
      </span>
      <p className="font-semibold">{title}</p>
      {children && <div className="mx-auto mt-1 max-w-prose text-sm text-muted">{children}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "Ocorreu um erro inesperado.";
  return (
    <Alert tone="danger" title="Não foi possível carregar" action={onRetry && <Button variant="secondary" onClick={onRetry}>Tentar novamente</Button>}>
      {message}
    </Alert>
  );
}

export function PageHeader({ title, subtitle, actions, crumb, back, eyebrow }: { title: string; subtitle?: ReactNode; actions?: ReactNode; crumb?: ReactNode; back?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <div className="mb-6 animate-fade-in">
      {back && <div className="mb-2">{back}</div>}
      {crumb && <div className="mb-2 text-xs text-muted">{crumb}</div>}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
          <h1 id="page-title" tabIndex={-1} className="text-[28px] leading-tight font-bold tracking-tight outline-none">{title}</h1>
          {subtitle && <div className="mt-1 text-sm text-muted">{subtitle}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
